import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { applyDocumentDirection } from './direction';
import { applyAppMetadata } from './appMetadata';

export const SUPPORTED_LANGUAGES = ['he', 'ar', 'en'];

// Translation bundles are NOT statically imported: the three JSONs were ~70%
// of the main chunk (each user only ever needs one, plus the 'he' fallback).
// webpack emits one async chunk per language (locale-he / locale-ar /
// locale-en); i18next pulls the active language (+ fallback) through this
// backend. changeLanguage() waits for the bundle before it switches and fires
// 'languageChanged', so switching never flashes raw keys. The first render
// waits on `i18nReady` (see index.js).
const lazyLocaleBackend = {
  type: 'backend',
  init() {},
  read(language, namespace, callback) {
    if (namespace !== 'translation' || !SUPPORTED_LANGUAGES.includes(language)) {
      // Unknown codes (e.g. a stale 'fr' or 'en-US' in localStorage) resolve
      // empty so i18next falls through to the next code / fallbackLng.
      callback(null, {});
      return;
    }
    import(
      /* webpackChunkName: "locale-[request]" */
      /* webpackMode: "lazy" */
      `./locales/${language}/translation.json`
    )
      .then((mod) => callback(null, mod.default || mod))
      .catch((err) => callback(err, null));
  },
};

const PUBLIC_CLIENT_PREFIXES = ['/intake/', '/confirm/', '/confirm-week/', '/sign-contract/', '/register/', '/public/', '/survey/', '/s/', '/g/'];

export function isPublicClientPath(pathname = (typeof window !== 'undefined' && window.location ? window.location.pathname : '')) {
  return PUBLIC_CLIENT_PREFIXES.some((p) => String(pathname || '').startsWith(p));
}

export const i18nReady = i18n
  .use(lazyLocaleBackend)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    fallbackLng: 'he',
    debug: false,
    // Resolve the detected language synchronously (i18n.language is set before
    // this module finishes) — only the bundle fetch itself is async.
    initAsync: false,
    interpolation: {
      escapeValue: false,
    },
    detection: {
      order: ['localStorage'],
      caches: ['localStorage'],
    },
  })
  // Public client links (intake form, confirmations, contract signing,
  // registration, shared lesson/survey pages) render in the LINK's language via
  // t(key, { lng }) without changing the app language, so they need every
  // bundle up front — otherwise an Arabic/English client sees the Hebrew
  // fallback. The logged-in app keeps loading one language.
  .then(() => (isPublicClientPath() ? i18n.loadLanguages(SUPPORTED_LANGUAGES) : undefined))
  // Never let a failed chunk fetch block the first render forever.
  .catch(() => {});

// One place that reacts to a language change, rather than each switcher
// remembering to. The mobile drawer's switcher called i18n.changeLanguage()
// without applying direction, so switching to Arabic there left the page LTR.
i18n.on('languageChanged', (lng) => {
  applyDocumentDirection(lng);
  applyAppMetadata(lng);
});

// The detector has already resolved a language by this point (initAsync:
// false sets i18n.language synchronously); the 'languageChanged' for the
// initial pick only fires once its bundle arrives, so apply direction now to
// avoid a LTR flash on the loader.
applyDocumentDirection(i18n.language);
applyAppMetadata(i18n.language);

export default i18n;
